'use client'

import React, { InputHTMLAttributes, useState, forwardRef } from 'react'
import { UseFormRegister, Control } from 'react-hook-form'

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { CheckIcon, EyeHideIcon, EyeIcon } from '@svgs'
import FieldError, { FIELD, FIELD_INVALID, FieldLabel } from './FieldParts'

export interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  name?: string
  register?: UseFormRegister<any>
  control?: Control<any>
  error?: any
  required?: boolean
  // Draws a red asterisk after the label; purely visual, does not touch validation
  requiredMark?: boolean
  // Adds a muted "(необов'язково)" after the label
  optional?: boolean
  type?: string
  refPhone?: any
  iconBefore?: JSX.Element
  iconAfter?: JSX.Element
  button?: JSX.Element
  isValid?: boolean
  inputClassName?: string
  variant?: 'accented'
  readOnly?: boolean
  validate?: (value: any) => true | string
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      className,
      inputClassName,
      label,
      placeholder,
      name,
      register,
      error,
      type = 'text',
      button,
      iconBefore,
      iconAfter,
      required = false,
      requiredMark,
      optional,
      isValid,
      variant,
      readOnly,
      validate,
      'aria-describedby': describedBy,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [showPassword, setShowPassword] = useState(false)
    // the message under the field belongs to it (read with the field)
    const errorId = name && error?.message ? `${name}-error` : undefined
    const describedByIds = [describedBy, errorId].filter(Boolean).join(' ') || undefined

    const togglePasswordVisibility = () => setShowPassword(!showPassword)

    const validateEmail = (value: string) => {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
      return emailRegex.test(value) || t('invalid_email_address')
    }

    const validatePhone = (value: string) => {
      // E.164: +[country code][subscriber number], max 15 digits, no spaces
      const e164 = /^\+[1-9]\d{1,14}$/
      return e164.test(value) || t('invalid_phone_e164')
    }

    const validatePassword = (value: string) => {
      if (value.length < 6) {
        return t('login.passwordMinLengthError')
      }
      return true
    }

    return (
      <div
        className={clsx(
          // min-w-0: inside a flex row the browser's intrinsic input width
          // (~170px) blocks shrinking and pushes the field out of its panel
          'flex flex-col w-full min-w-0 relative text-base text-ink',
          className,
          error && 'error',
        )}
      >
        {label && (
          <FieldLabel
            className="mb-1.5"
            label={label}
            required={required || requiredMark}
            optional={optional}
            htmlFor={name}
          />
        )}
        {type === 'password' ? (
          <div className="flex w-full relative">
            {iconBefore && (
              <span className="icon fill-ink absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none z-10">
                {iconBefore}
              </span>
            )}
            {iconAfter && (
              <span className="icon fill-ink absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none z-10">
                {iconAfter}
              </span>
            )}
            <input
              ref={ref}
              className={clsx(
                FIELD,
                iconBefore && '!pl-10',
                iconAfter && '!pr-10',
                // room for the show/hide button: typed text ran under the eye
                !readOnly && '!pr-11',
                inputClassName,
                variant === 'accented' &&
                  'min-h-11 h-11 p-0 py-[.6875rem] rounded-none bg-transparent border-0 border-t !border-t-transparent border-b !border-control border-solid !text-base font-normal focus:!border-b-ink',
                error && FIELD_INVALID,
              )}
              placeholder={placeholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={describedByIds}
              type={showPassword ? 'text' : type}
              id={name}
              readOnly={readOnly}
              required={required}
              {...(register &&
                name &&
                register(name, {
                  required: required ? t('login.requiredField') : false,
                  validate: validate ?? (type === 'password' ? validatePassword : undefined),
                }))}
              {...props}
            />
            {button && button}
            {!readOnly && (
              // a real button: the eye was a clickable div a keyboard could
              // not reach and a screen reader could not name
              <button
                type="button"
                aria-label={showPassword ? t('password_toggle.hide') : t('password_toggle.show')}
                aria-pressed={showPassword}
                onClick={togglePasswordVisibility}
                className="absolute top-1/2 -translate-y-1/2 right-1 z-[5] flex items-center justify-center w-9 h-9 rounded-lg fill-ink-2 hover:fill-ink focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
              >
                <span className="icon w-5 h-5">{showPassword ? <EyeHideIcon /> : <EyeIcon />}</span>
              </button>
            )}
          </div>
        ) : (
          <div className="flex w-full relative">
            {iconBefore && (
              <span className="icon fill-ink absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none z-10">
                {iconBefore}
              </span>
            )}
            {iconAfter && (
              <span className="icon fill-ink absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none z-10">
                {iconAfter}
              </span>
            )}
            <input
              ref={ref}
              className={clsx(
                FIELD,
                iconBefore && '!pl-10',
                iconAfter && '!pr-10',
                inputClassName,
                variant === 'accented' &&
                  'min-h-11 h-11 p-0 py-[.6875rem] rounded-none bg-transparent border-0 border-t !border-t-transparent border-b !border-control border-solid !text-base font-normal focus:!border-b-ink',
                error && FIELD_INVALID,
              )}
              placeholder={placeholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={describedByIds}
              type={type}
              id={name}
              readOnly={readOnly}
              required={required}
              {...(register &&
                name &&
                register(name, {
                  required: required ? t('login.requiredField') : false,
                  validate:
                    validate ??
                    (type === 'email'
                      ? validateEmail
                      : type === 'tel'
                        ? validatePhone
                        : type === 'password'
                          ? validatePassword
                          : undefined),
                }))}
              {...props}
            />
            {button && button}
            {isValid && (
              <div
                className={'w-3 h-3 icon fill-ink absolute right-6 top-1/2 -translate-y-1/2 z-10'}
              >
                <CheckIcon />
              </div>
            )}
          </div>
        )}
        {error?.message && <FieldError id={errorId} message={error.message} />}
      </div>
    )
  },
)

InputField.displayName = 'InputField'

export default InputField
